import { baseApi } from '../baseApi';
import { API_PATHS, API_HEADERS, API_CONTENT_TYPES, EVENT_SCOPE_MY_EVENTS } from '@/constants';
import type {
  ApiResponse,
  PaginatedResponse,
  EventSummary,
  EventData,
  GetEventsParams,
  GetEventByIdParams,
  EventId,
  BuildingId,
  PatchEventParams,
  CreateEventRequest,
  CreateEventResponse,
  PatchEventRequest,
  PatchEventResponse,
  SubmitEventRequest,
  SubmitEventResponse,
  CancelEventRequest,
  PlannerCancelEventRequest,
  ConfirmCancellationRequest,
  CancelEventResponse,
  EventMetricsResult,
  MetricsRequestParams,
  PlannerMetricsExportParams,
  PeriodMetrics,
} from '@store/models';
import { normalizeEventState } from '@/types/events.types';

/** Coerces a possibly missing/null server value into a finite number (default 0). */
const toFiniteNumber = (value: unknown): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Normalizes a single metrics period so every numeric field the UI renders is a
 * finite number and every flag is a boolean. Guards against partial/older server
 * payloads that omit or null a field — otherwise the report screen would call
 * `.toLocaleString()`/`.toFixed()` on `undefined` and crash the whole view.
 */
const normalizePeriod = (period: Partial<PeriodMetrics> | null | undefined): PeriodMetrics => ({
  label: period?.label,
  startDate: period?.startDate ?? '',
  endDate: period?.endDate ?? '',
  requestedMeetings: toFiniteNumber(period?.requestedMeetings),
  completedMeetings: toFiniteNumber(period?.completedMeetings),
  requestedAttendees: toFiniteNumber(period?.requestedAttendees),
  completedAttendees: toFiniteNumber(period?.completedAttendees),
  meetingUtilizationRate: toFiniteNumber(period?.meetingUtilizationRate),
  meetingUtilizationApplicable: Boolean(period?.meetingUtilizationApplicable),
  totalBookedHours: toFiniteNumber(period?.totalBookedHours),
  bookableSpaceCount: toFiniteNumber(period?.bookableSpaceCount),
  daysInRange: toFiniteNumber(period?.daysInRange),
  totalAvailableHours: toFiniteNumber(period?.totalAvailableHours),
  utilizationHoursPercentage: toFiniteNumber(period?.utilizationHoursPercentage),
  utilizationHoursApplicable: Boolean(period?.utilizationHoursApplicable),
});
export interface BatchEventItem {
  eventId: string;
  buildingId: string;
}

export interface BatchEventAssignment {
  plannerId: string;
  plannerName: string;
}

export interface BatchEventActionArg {
  action: 'CLAIM' | 'HANDOFF';
  events: BatchEventItem[];
  assignment: BatchEventAssignment;
}

export interface BatchEventResultItem {
  eventId: string;
  status: 'SUCCESS' | 'FAILED';
  newState?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface BatchEventActionResponseData {
  results: BatchEventResultItem[];
}

interface CreateEventArg {
  body: CreateEventRequest;
  buildingId: BuildingId;
}

interface DeleteDraftEventArg {
  eventId: EventId;
  buildingId: BuildingId;
}

export function buildDeleteDraftEventQuery({ eventId }: DeleteDraftEventArg) {
  return {
    url: `${API_PATHS.EVENTS}/${eventId}?softDelete=false`,
    method: 'DELETE' as const,
  };
}

export function buildGetEventsQuery(params?: GetEventsParams) {
  const searchParams = new URLSearchParams({ scope: EVENT_SCOPE_MY_EVENTS });
  if (params) {
    if (params.status !== undefined) searchParams.set('status', params.status);
    if (params.tab !== undefined) searchParams.set('tab', params.tab);
    if (params.startDate !== undefined) searchParams.set('startDate', params.startDate);
    if (params.endDate !== undefined) searchParams.set('endDate', params.endDate);
    if (params.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));
    if (params.continuationToken !== undefined)
      searchParams.set('continuationToken', params.continuationToken);
    if (params.sortBy !== undefined) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder !== undefined) searchParams.set('sortOrder', params.sortOrder);
  }
  return {
    url: `${API_PATHS.EVENTS}?${searchParams.toString()}`,
  };
}

export function buildGetPlannerEventsQuery(params?: GetEventsParams) {
  const searchParams = new URLSearchParams();
  if (params) {
    if (params.status !== undefined) searchParams.set('status', params.status);
    if (params.tab !== undefined) searchParams.set('tab', params.tab);
    if (params.startDate !== undefined) searchParams.set('startDate', params.startDate);
    if (params.endDate !== undefined) searchParams.set('endDate', params.endDate);
    if (params.buildingId !== undefined)
      searchParams.set('buildingId', params.buildingId as string);
    if (params.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));
    if (params.continuationToken !== undefined)
      searchParams.set('continuationToken', params.continuationToken);
    if (params.sortBy !== undefined) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder !== undefined) searchParams.set('sortOrder', params.sortOrder);
    if (params.owner !== undefined) searchParams.set('owner', params.owner);
    if (params.submittedStartDate !== undefined)
      searchParams.set('submittedStartDate', params.submittedStartDate);
    if (params.submittedEndDate !== undefined)
      searchParams.set('submittedEndDate', params.submittedEndDate);
    if (params.search !== undefined) searchParams.set('search', params.search);
    if (params.dateFormat !== undefined) searchParams.set('dateFormat', params.dateFormat);
  }
  const qs = searchParams.toString();
  return {
    url: qs ? `${API_PATHS.EVENTS}?${qs}` : API_PATHS.EVENTS,
  };
}

const EVENTS_EXPORT_PATH = `${API_PATHS.EVENTS}/export`;
const FALLBACK_EXPORT_FILENAME = 'events-export.xlsx';

export function buildGetEventsExportQuery(params?: GetEventsParams) {
  const planner = buildGetPlannerEventsQuery(params);
  const sep = planner.url.indexOf('?');
  const qs = sep >= 0 ? planner.url.slice(sep + 1) : '';
  return {
    url: qs ? `${EVENTS_EXPORT_PATH}?${qs}` : EVENTS_EXPORT_PATH,
  };
}

export function parseFilenameFromContentDisposition(header: string | null): string {
  if (!header) return FALLBACK_EXPORT_FILENAME;
  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      // fall through
    }
  }
  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (quoted?.[1]) return quoted[1].trim();
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare?.[1]) return bare[1].trim();
  return FALLBACK_EXPORT_FILENAME;
}

const METRICS_EXPORT_PATH = `${API_PATHS.EVENTS_METRICS}/export`;

export function buildPlannerMetricsExportQuery(params: PlannerMetricsExportParams) {
  const searchParams = new URLSearchParams();
  if (params.buildingIds && params.buildingIds.length > 0) {
    searchParams.set('buildingIds', params.buildingIds.join(','));
  }
  searchParams.set('startDate', params.startDate);
  searchParams.set('endDate', params.endDate);
  if (params.dateFormat !== undefined) searchParams.set('dateFormat', params.dateFormat);
  return { url: `${METRICS_EXPORT_PATH}?${searchParams.toString()}` };
}

export interface EventsExportResult {
  blob: Blob;
  filename: string;
}

export interface SubmitEventArg {
  eventId: EventId;
  buildingId: BuildingId;
  body?: SubmitEventRequest;
  etag?: string;
}

export interface EtagMeta {
  etag?: string;
}

export function buildSubmitEventQuery({ eventId, body, etag }: SubmitEventArg) {
  const headers: Record<string, string> = {};

  if (etag) {
    headers[API_HEADERS.IF_MATCH] = etag;
  }

  return {
    url: `${API_PATHS.EVENTS}/${eventId}/submit`,
    method: 'POST' as const,
    headers,
    body: body ?? {},
  };
}

export const eventServiceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<PaginatedResponse<EventSummary>, GetEventsParams | void>({
      query: (params) => buildGetEventsQuery(params ?? undefined),
      transformResponse: (
        response: ApiResponse<{
          data: EventSummary[];
          pagination: {
            pageSize: number;
            continuationToken?: string;
            hasMore: boolean;
            totalCount?: number;
          };
        }>
      ) => {
        if (!response.success) {
          return response as unknown as PaginatedResponse<EventSummary>;
        }
        const normalized = response.data.data.map((e) => ({
          ...e,
          eventState: normalizeEventState(e.eventState),
        }));
        return {
          success: true as const,
          data: normalized,
          pagination: {
            ...response.data.pagination,
            totalCount: response.data.pagination.totalCount ?? normalized.length,
          },
          meta: response.meta,
        };
      },
      providesTags: ['Events'],
    }),

    getEventById: builder.query<ApiResponse<EventData> & EtagMeta, GetEventByIdParams>({
      query: ({ eventId, ...rest }) => {
        const searchParams = new URLSearchParams();
        if (rest.includeHistory !== undefined) {
          searchParams.set('includeHistory', String(rest.includeHistory));
        }
        const qs = searchParams.toString();
        const url = `${API_PATHS.EVENTS}/${eventId}`;
        return {
          url: qs ? `${url}?${qs}` : url,
        };
      },
      transformResponse: (response: ApiResponse<EventData>, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      // Event details must always be fresh — drop cache immediately so every
      // open of the detail panel triggers a network call.
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { eventId }) => [{ type: 'EventDetail', id: eventId }],
    }),

    createEvent: builder.mutation<CreateEventResponse & EtagMeta, CreateEventArg>({
      query: ({ body }) => ({
        url: API_PATHS.EVENTS,
        method: 'POST',
        body,
      }),
      transformResponse: (response: CreateEventResponse, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events'],
    }),

    patchEvent: builder.mutation<
      PatchEventResponse & EtagMeta,
      { params: PatchEventParams; body: PatchEventRequest }
    >({
      query: ({ params, body }) => {
        const url = `${API_PATHS.EVENTS}/${params.eventId}`;
        const headers: Record<string, string> = {};
        if (params.etag) {
          headers[API_HEADERS.IF_MATCH] = params.etag;
        }
        return {
          url,
          method: 'PATCH',
          body,
          headers,
        };
      },
      transformResponse: (response: PatchEventResponse, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    // TODO: Re-enable when backend submit endpoint is available
    // submitEvent: builder.mutation<SubmitEventResponse, { eventId: EventId; body: SubmitEventRequest }>({...}),

    submitEvent: builder.mutation<SubmitEventResponse & EtagMeta, SubmitEventArg>({
      query: buildSubmitEventQuery,
      transformResponse: (response: SubmitEventResponse, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    deleteDraftEvent: builder.mutation<void, DeleteDraftEventArg>({
      query: buildDeleteDraftEventQuery,
      invalidatesTags: ['Events'],
    }),

    // TODO: Re-enable when backend cancel endpoint is available
    // cancelEvent: builder.mutation<CancelEventResponse, { eventId: EventId; body: CancelEventRequest }>({...}),

    cancelEvent: builder.mutation<
      CancelEventResponse & EtagMeta,
      {
        eventId: EventId;
        buildingId: BuildingId;
        body: CancelEventRequest | PlannerCancelEventRequest | ConfirmCancellationRequest;
        etag?: string;
      }
    >({
      query: ({ eventId, body, etag }) => {
        const headers: Record<string, string> = {};
        if (etag) {
          headers[API_HEADERS.IF_MATCH] = etag;
        }
        return {
          url: `${API_PATHS.EVENTS}/${eventId}/actions`,
          method: 'POST',
          body,
          headers,
        };
      },
      transformResponse: (response: CancelEventResponse, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    batchEventAction: builder.mutation<
      ApiResponse<BatchEventActionResponseData>,
      BatchEventActionArg
    >({
      query: ({ action, events, assignment }) => ({
        url: API_PATHS.EVENTS_BATCH_ACTIONS,
        method: 'POST',
        body: { action, events, assignment },
      }),
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    getPlannerEvents: builder.query<PaginatedResponse<EventSummary>, GetEventsParams | void>({
      query: (params) => buildGetPlannerEventsQuery(params ?? undefined),
      transformResponse: (
        response: ApiResponse<{
          data: EventSummary[];
          pagination: {
            pageSize: number;
            continuationToken?: string;
            hasMore: boolean;
            totalCount?: number;
          };
        }>
      ) => {
        if (!response.success) {
          return response as unknown as PaginatedResponse<EventSummary>;
        }
        const normalized = response.data.data.map((e) => ({
          ...e,
          eventState: normalizeEventState(e.eventState),
        }));
        return {
          success: true as const,
          data: normalized,
          pagination: {
            ...response.data.pagination,
            totalCount: response.data.pagination.totalCount ?? normalized.length,
          },
          meta: response.meta,
        };
      },
      providesTags: ['Events'],
    }),

    // Eagerly fetches every page for the given params and returns the full
    // accumulated list. Use only where the caller needs all events at once
    // (e.g. calendar views over a fixed date range). For paginated UIs that
    // load batches on demand, use getPlannerEvents instead.
    getAllPlannerEvents: builder.query<PaginatedResponse<EventSummary>, GetEventsParams | void>({
      async queryFn(params, _queryApi, _extraOptions, baseQuery) {
        const allData: EventSummary[] = [];
        let continuationToken: string | undefined;
        let totalCount = 0;
        const pageSize = params?.pageSize ?? 100;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const queryParams: GetEventsParams = {
            ...(params ?? {}),
            pageSize,
            ...(continuationToken ? { continuationToken } : {}),
          };
          const result = await baseQuery(buildGetPlannerEventsQuery(queryParams));

          if (result.error) {
            return { error: result.error };
          }

          const response = result.data as ApiResponse<{
            data: EventSummary[];
            pagination: {
              pageSize: number;
              continuationToken?: string;
              hasMore: boolean;
              totalCount?: number;
            };
          }>;

          if (!response.success) {
            return { data: response as unknown as PaginatedResponse<EventSummary> };
          }

          const normalized = response.data.data.map((e) => ({
            ...e,
            eventState: normalizeEventState(e.eventState),
          }));
          allData.push(...normalized);
          totalCount = response.data.pagination.totalCount ?? allData.length;

          if (!response.data.pagination.hasMore || !response.data.pagination.continuationToken) {
            break;
          }
          continuationToken = response.data.pagination.continuationToken;
        }

        return {
          data: {
            success: true as const,
            data: allData,
            pagination: {
              pageSize,
              hasMore: false,
              totalCount,
            },
          },
        };
      },
      // Calendar views must never serve stale data — drop cache immediately so
      // every mount/arg-change triggers a fresh network call.
      keepUnusedDataFor: 0,
      providesTags: ['Events'],
    }),

    confirmEvent: builder.mutation<
      ApiResponse<{ eventId: string; newState: string }> & EtagMeta,
      { eventId: EventId; buildingId: BuildingId; comments?: string; etag?: string }
    >({
      query: ({ eventId, comments, etag }) => {
        const headers: Record<string, string> = {};
        if (etag) {
          headers[API_HEADERS.IF_MATCH] = etag;
        }
        return {
          url: `${API_PATHS.EVENTS}/${eventId}/actions`,
          method: 'POST',
          body: { action: 'BOOK', comments: comments ?? '' },
          headers,
        };
      },
      transformResponse: (response: ApiResponse<{ eventId: string; newState: string }>, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    getEventsExport: builder.query<EventsExportResult, GetEventsParams | void>({
      query: (params) => {
        const headers: Record<string, string> = {
          [API_HEADERS.CONTENT_TYPE]: API_CONTENT_TYPES.XLSX,
        };
        return {
          url: buildGetEventsExportQuery(params ?? undefined).url,
          headers,
          responseHandler: async (response: Response): Promise<unknown> => {
            if (!response.ok) {
              const ct = response.headers.get('content-type') ?? '';
              if (ct.includes('json')) {
                try {
                  return (await response.json()) as unknown;
                } catch {
                  /* fall through */
                }
              }
              try {
                return await response.text();
              } catch {
                return undefined;
              }
            }
            return response.blob();
          },
        };
      },
      transformResponse: (blob: Blob, meta) => ({
        blob,
        filename: parseFilenameFromContentDisposition(
          meta?.response?.headers?.get('Content-Disposition') ?? null
        ),
      }),
    }),

    declineEvent: builder.mutation<
      ApiResponse<{ eventId: string; newState: string }> & EtagMeta,
      { eventId: EventId; buildingId: BuildingId; comments: string; etag?: string }
    >({
      query: ({ eventId, comments, etag }) => {
        const headers: Record<string, string> = {};
        if (etag) {
          headers[API_HEADERS.IF_MATCH] = etag;
        }
        return {
          url: `${API_PATHS.EVENTS}/${eventId}/actions`,
          method: 'POST',
          body: { action: 'DECLINE', comments },
          headers,
        };
      },
      transformResponse: (response: ApiResponse<{ eventId: string; newState: string }>, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    markNoOccurrence: builder.mutation<
      ApiResponse<{ eventId: string; newState: string }> & EtagMeta,
      { eventId: EventId; buildingId: BuildingId; etag?: string }
    >({
      query: ({ eventId, etag }) => {
        const headers: Record<string, string> = {};
        if (etag) {
          headers[API_HEADERS.IF_MATCH] = etag;
        }
        return {
          url: `${API_PATHS.EVENTS}/${eventId}/actions`,
          method: 'POST',
          body: { action: 'NO_OCCURRENCE' },
          headers,
        };
      },
      transformResponse: (response: ApiResponse<{ eventId: string; newState: string }>, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
      invalidatesTags: ['Events', 'EventDetail'],
    }),

    resendSurvey: builder.mutation<
      ApiResponse<{ eventId: string; newState: string }> & EtagMeta,
      { eventId: EventId; buildingId: BuildingId }
    >({
      query: ({ eventId }) => ({
        url: `${API_PATHS.EVENTS}/${eventId}/survey/resend`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ eventId: string; newState: string }>, meta) => {
        const etag = meta?.response?.headers?.get(API_HEADERS.ETAG) ?? undefined;
        return { ...response, etag };
      },
    }),

    getEventHistory: builder.query<
      {
        items: import('@store/models').EventHistoryEntry[];
        totalCount: number;
        skip: number;
        top: number;
        hasMore: boolean;
      },
      { eventId: string; buildingId: string; skip?: number; top?: number }
    >({
      query: ({ eventId, skip = 0, top = 20 }) => ({
        url: `${API_PATHS.EVENTS}/${eventId}/history`,
        params: { skip, top },
      }),
      transformResponse: (
        response: ApiResponse<{
          items: import('@store/models').EventHistoryEntry[];
          totalCount: number;
          skip: number;
          top: number;
          hasMore: boolean;
        }>
      ) => response.data,
    }),

    getEventMetrics: builder.query<EventMetricsResult, MetricsRequestParams>({
      query: (body) => ({
        url: API_PATHS.EVENTS_METRICS,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<EventMetricsResult> | null | undefined) => {
        // A successful (2xx) response can still carry an empty/null body (e.g. a
        // 204-style no-content or a proxy-stripped body). Guard against it so the
        // transform never throws — a throw here surfaces as isError on the query
        // even though the HTTP call succeeded, showing a misleading error state.
        if (!response?.success || !response.data) {
          return { periods: [] };
        }
        // Normalize every period so the report screen never receives an undefined
        // numeric/flag field (which would crash the render on `.toLocaleString()`).
        return { periods: (response.data.periods ?? []).map(normalizePeriod) };
      },
      providesTags: ['EventMetrics'],
    }),

    getPlannerMetricsExport: builder.query<EventsExportResult, PlannerMetricsExportParams>({
      query: (params) => {
        const headers: Record<string, string> = {
          [API_HEADERS.CONTENT_TYPE]: API_CONTENT_TYPES.XLSX,
        };
        return {
          url: buildPlannerMetricsExportQuery(params).url,
          headers,
          responseHandler: async (response: Response): Promise<unknown> => {
            if (!response.ok) {
              const ct = response.headers.get('content-type') ?? '';
              if (ct.includes('json')) {
                try {
                  return (await response.json()) as unknown;
                } catch {
                  /* fall through */
                }
              }
              try {
                return await response.text();
              } catch {
                return undefined;
              }
            }
            return response.blob();
          },
        };
      },
      transformResponse: (blob: Blob, meta) => ({
        blob,
        filename: parseFilenameFromContentDisposition(
          meta?.response?.headers?.get('Content-Disposition') ?? null
        ),
      }),
    }),
  }),
});
export const {
  useGetEventsQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  usePatchEventMutation,
  useSubmitEventMutation,
  useDeleteDraftEventMutation,
  useCancelEventMutation,
  useBatchEventActionMutation,
  useGetPlannerEventsQuery,
  useGetAllPlannerEventsQuery,
  useConfirmEventMutation,
  useDeclineEventMutation,
  useMarkNoOccurrenceMutation,
  useResendSurveyMutation,
  useLazyGetEventHistoryQuery,
  useGetEventHistoryQuery,
  useLazyGetEventsExportQuery,
  useGetEventMetricsQuery,
  useLazyGetPlannerMetricsExportQuery,
} = eventServiceApi;
